import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inputCls = "input-field";
const labelCls = "label-field";

const SubmitButton = ({ loading, children, testid, dark = false }) => (
  <button
    type="submit"
    disabled={loading}
    data-testid={testid}
    className={`w-full flex items-center justify-center gap-2 rounded-full py-3.5 text-sm font-bold transition-all disabled:opacity-60 ${
      dark
        ? "bg-brand-gold text-brand-deep hover:bg-brand-wheat"
        : "bg-brand-deep text-white hover:bg-brand-forest"
    }`}
  >
    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
    {children}
  </button>
);

export function DistributorForm({ dark = false }) {
  const empty = { name: "", business_name: "", phone: "", whatsapp: "", district: "", city: "", business_type: "", products_interested: [], estimated_requirement: "", message: "" };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const { products } = useSite();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleProduct = (name) =>
    setForm((f) => ({
      ...f,
      products_interested: f.products_interested.includes(name)
        ? f.products_interested.filter((p) => p !== name)
        : [...f.products_interested, name],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", { ...form, type: "distributor" });
      toast.success("Thank you for your interest in ICROPSIA. Our team will review your enquiry and contact you shortly.");
      setForm(empty);
    } catch {
      toast.error("Could not submit your enquiry. Please try again or reach us on WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="distributor-lead-form" className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls} htmlFor="df-name">Full Name *</label>
          <input id="df-name" data-testid="distributor-name-input" required className={inputCls} value={form.name} onChange={set("name")} placeholder="Your full name" />
        </div>
        <div>
          <label className={labelCls} htmlFor="df-business">Business / Shop Name</label>
          <input id="df-business" data-testid="distributor-business-input" className={inputCls} value={form.business_name} onChange={set("business_name")} placeholder="e.g. Sharma Agro Store" />
        </div>
        <div>
          <label className={labelCls} htmlFor="df-phone">Mobile Number *</label>
          <input id="df-phone" data-testid="distributor-phone-input" required type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="10-digit mobile number" />
        </div>
        <div>
          <label className={labelCls} htmlFor="df-whatsapp">WhatsApp Number</label>
          <input id="df-whatsapp" data-testid="distributor-whatsapp-input" type="tel" className={inputCls} value={form.whatsapp} onChange={set("whatsapp")} placeholder="If different from mobile" />
        </div>
        <div>
          <label className={labelCls} htmlFor="df-district">District *</label>
          <input id="df-district" data-testid="distributor-district-input" required className={inputCls} value={form.district} onChange={set("district")} placeholder="e.g. Patna" />
        </div>
        <div>
          <label className={labelCls} htmlFor="df-city">City / Town</label>
          <input id="df-city" data-testid="distributor-city-input" className={inputCls} value={form.city} onChange={set("city")} placeholder="Your city or town" />
        </div>
        <div>
          <label className={labelCls}>Business Type *</label>
          <Select required value={form.business_type} onValueChange={(v) => setForm((f) => ({ ...f, business_type: v }))}>
            <SelectTrigger data-testid="distributor-business-type-select" className="input-field h-auto">
              <SelectValue placeholder="Select business type" />
            </SelectTrigger>
            <SelectContent>
              {["Retailer", "Distributor", "Dealer", "Wholesaler", "Other"].map((t) => (
                <SelectItem key={t} value={t} data-testid={`business-type-${t.toLowerCase()}`}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className={labelCls}>Estimated Requirement</label>
          <Select value={form.estimated_requirement} onValueChange={(v) => setForm((f) => ({ ...f, estimated_requirement: v }))}>
            <SelectTrigger data-testid="distributor-requirement-select" className="input-field h-auto">
              <SelectValue placeholder="Select quantity range" />
            </SelectTrigger>
            <SelectContent>
              {["Up to 50 bags", "50 – 200 bags", "200 – 500 bags", "500+ bags", "Not sure yet"].map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <label className={labelCls}>Products Interested In</label>
        <div className="flex flex-wrap gap-2">
          {products.map((p) => (
            <button
              type="button"
              key={p.slug}
              data-testid={`distributor-interest-${p.slug}`}
              onClick={() => toggleProduct(p.name)}
              className={`rounded-full px-4 py-2 text-sm font-semibold border transition-all ${
                form.products_interested.includes(p.name)
                  ? "bg-brand-deep text-white border-brand-deep"
                  : "bg-white text-brand-deep/70 border-brand-deep/20 hover:border-brand-deep/50"
              }`}
            >
              {p.name}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={labelCls} htmlFor="df-message">Message</label>
        <textarea id="df-message" data-testid="distributor-message-input" rows={3} className={inputCls} value={form.message} onChange={set("message")} placeholder="Anything you'd like us to know about your business" />
      </div>
      <SubmitButton loading={loading} testid="distributor-submit-button" dark={dark}>
        Submit Partnership Enquiry
      </SubmitButton>
    </form>
  );
}

export function BulkForm() {
  const empty = { name: "", business_name: "", phone: "", whatsapp: "", city: "", product: "", quantity: "", preferred_contact: "", message: "" };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const { products } = useSite();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", { ...form, type: "bulk" });
      toast.success("Bulk supply request received. Our team will contact you shortly.");
      setForm(empty);
    } catch {
      toast.error("Could not submit your request. Please try again or reach us on WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="bulk-order-form" className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls} htmlFor="bf-name">Name *</label>
          <input id="bf-name" data-testid="bulk-name-input" required className={inputCls} value={form.name} onChange={set("name")} placeholder="Your full name" />
        </div>
        <div>
          <label className={labelCls} htmlFor="bf-business">Company / Business</label>
          <input id="bf-business" data-testid="bulk-business-input" className={inputCls} value={form.business_name} onChange={set("business_name")} placeholder="Business name" />
        </div>
        <div>
          <label className={labelCls} htmlFor="bf-phone">Phone *</label>
          <input id="bf-phone" data-testid="bulk-phone-input" required type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="10-digit mobile number" />
        </div>
        <div>
          <label className={labelCls} htmlFor="bf-whatsapp">WhatsApp</label>
          <input id="bf-whatsapp" data-testid="bulk-whatsapp-input" type="tel" className={inputCls} value={form.whatsapp} onChange={set("whatsapp")} placeholder="WhatsApp number" />
        </div>
        <div>
          <label className={labelCls} htmlFor="bf-location">Location *</label>
          <input id="bf-location" data-testid="bulk-location-input" required className={inputCls} value={form.city} onChange={set("city")} placeholder="District / Town" />
        </div>
        <div>
          <label className={labelCls}>Product *</label>
          <Select required value={form.product} onValueChange={(v) => setForm((f) => ({ ...f, product: v }))}>
            <SelectTrigger data-testid="bulk-product-select" className="input-field h-auto">
              <SelectValue placeholder="Select product" />
            </SelectTrigger>
            <SelectContent>
              {products.map((p) => (
                <SelectItem key={p.slug} value={p.name}>{p.name} — {p.crop}</SelectItem>
              ))}
              <SelectItem value="Multiple / Not sure">Multiple / Not sure</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className={labelCls} htmlFor="bf-quantity">Quantity Required *</label>
          <input id="bf-quantity" data-testid="bulk-quantity-input" required className={inputCls} value={form.quantity} onChange={set("quantity")} placeholder="e.g. 100 bags" />
        </div>
        <div>
          <label className={labelCls}>Preferred Contact Method</label>
          <Select value={form.preferred_contact} onValueChange={(v) => setForm((f) => ({ ...f, preferred_contact: v }))}>
            <SelectTrigger data-testid="bulk-contact-method-select" className="input-field h-auto">
              <SelectValue placeholder="How should we reach you?" />
            </SelectTrigger>
            <SelectContent>
              {["Phone Call", "WhatsApp", "Email"].map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <label className={labelCls} htmlFor="bf-message">Message</label>
        <textarea id="bf-message" data-testid="bulk-message-input" rows={3} className={inputCls} value={form.message} onChange={set("message")} placeholder="Delivery location, timelines, or anything else" />
      </div>
      <SubmitButton loading={loading} testid="bulk-submit-button">Request Bulk Supply</SubmitButton>
    </form>
  );
}

export function ContactForm() {
  const empty = { name: "", phone: "", message: "" };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", { ...form, type: "contact" });
      toast.success("Message received. The ICROPSIA team will get back to you shortly.");
      setForm(empty);
    } catch {
      toast.error("Could not send your message. Please try again or reach us on WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="contact-form" className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls} htmlFor="cf-name">Name *</label>
          <input id="cf-name" data-testid="contact-name-input" required className={inputCls} value={form.name} onChange={set("name")} placeholder="Your full name" />
        </div>
        <div>
          <label className={labelCls} htmlFor="cf-phone">Phone *</label>
          <input id="cf-phone" data-testid="contact-phone-input" required type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="10-digit mobile number" />
        </div>
      </div>
      <div>
        <label className={labelCls} htmlFor="cf-message">Message *</label>
        <textarea id="cf-message" data-testid="contact-message-input" required rows={5} className={inputCls} value={form.message} onChange={set("message")} placeholder="How can we help?" />
      </div>
      <SubmitButton loading={loading} testid="contact-submit-button">Send Message</SubmitButton>
    </form>
  );
}

export function EnquiryDialog({ productName, triggerClassName, triggerTestid, children }) {
  const [open, setOpen] = useState(false);
  const empty = { name: "", phone: "", message: "" };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", { ...form, type: "product", product: productName });
      toast.success(`Enquiry for ${productName} received. Our team will contact you shortly.`);
      setForm(empty);
      setOpen(false);
    } catch {
      toast.error("Could not submit your enquiry. Please try again or reach us on WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={triggerTestid} className={triggerClassName}>
          {children || "Enquire Now"}
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md bg-brand-ivory" data-testid="product-enquiry-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-brand-deep text-xl">Enquire About {productName}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div>
            <label className={labelCls} htmlFor="eq-name">Name *</label>
            <input id="eq-name" data-testid="enquiry-name-input" required className={inputCls} value={form.name} onChange={set("name")} placeholder="Your full name" />
          </div>
          <div>
            <label className={labelCls} htmlFor="eq-phone">Phone *</label>
            <input id="eq-phone" data-testid="enquiry-phone-input" required type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="10-digit mobile number" />
          </div>
          <div>
            <label className={labelCls} htmlFor="eq-message">Message</label>
            <textarea id="eq-message" data-testid="enquiry-message-input" rows={3} className={inputCls} value={form.message} onChange={set("message")} placeholder={`I am interested in ${productName}. Please share product and availability details.`} />
          </div>
          <SubmitButton loading={loading} testid="enquiry-submit-button">Submit Enquiry</SubmitButton>
        </form>
      </DialogContent>
    </Dialog>
  );
}
